import { computed, makeObservable } from 'mobx';
import {
    apiGetGamstopStateRequest,
    ApiGetGamstopStateResponse200Type,
    Response500Type,
} from 'src/api_openapi/v2/compliance/apiGetGamstopState';
import { Common } from 'src/AppState/Common';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { GamstopStatusType } from './types';
import { DateTime } from 'src_common/utils/time/time';

type ApiGetGamstopStateResponse500Type = Response500Type;
type ApiGamstopResponseType =
    | {
          status: 'success';
          body: ApiGetGamstopStateResponse200Type;
      }
    | {
          status: 'error';
          body: ApiGetGamstopStateResponse500Type;
      };

export class GamstopUserStore {
    private readonly gamstopResource: Resource<ApiGamstopResponseType | null>;

    public constructor(
        private readonly common: Common,
        account_id: number
    ) {
        makeObservable(this);

        this.gamstopResource = new Resource(async (): Promise<ApiGamstopResponseType | null> => {
            const response = await apiGetGamstopStateRequest(
                this.common.appEnvParams.gatewayUrl,
                {
                    account_id,
                },
                {
                    authorization: `Bearer ${this.common.session.currentJwt}`,
                }
            );

            if (response.status === 200) {
                return {
                    status: 'success',
                    body: response.body,
                };
            }

            if (response.status === 500) {
                return {
                    status: 'error',
                    body: response.body,
                };
            }

            return null;
        });
    }

    @computed private get data(): ApiGetGamstopStateResponse200Type | 'loading' | 'error' {
        const data = this.gamstopResource.get();

        if (data.type === 'loading') {
            return 'loading';
        }

        if (data.type === 'error') {
            return 'error';
        }

        if (data.value === null || data.value.status === 'error') {
            return 'error';
        }

        return data.value.body;
    }

    @computed public get isLoading(): boolean {
        return this.data === 'loading';
    }

    @computed public get status(): GamstopStatusType {
        const data = this.data;

        if (data === 'loading') {
            return 'loading';
        }

        if (data === 'error') {
            return 'error';
        }

        if (data.gamstop === undefined) {
            return 'unknown';
        }

        return data.gamstop.status;
    }

    @computed public get updatedAt(): string | null {
        const data = this.data;

        if (data === 'loading' || data === 'error') {
            return null;
        }

        if (data.gamstop === undefined) {
            return null;
        }

        return DateTime.fromUTC(data.gamstop.restriction.updatedAt)?.format('DD MMM YYYY, hh:mm:ss A') ?? null;
    }
}
